<template>
  <div class="row" v-if="workOrder">
    <div class="col-sm-3">
      <h1>{{ workOrder.orderNumber }}</h1>
    </div>
    <div class="col-sm-6 d-none d-lg-block"><h3>Kontrolni list preverjanja delovanja termometrov</h3></div>
    <div class="col-sm-3">
      <h2><OrderBadge :workOrder="workOrder" /></h2>
    </div>
  </div>

  <div class="row mb-3 mt-3" v-if="workOrder">
    <div class="col-md-8"></div>
    <div class="col-md-4">
      <div class="card mb-3" v-if="device">
        <div class="card-body">
          <p class="card-text">Naprava: {{ device.deviceNumber }}</p>
          <p class="card-text">Nalepka: {{ device.label }}</p>
          <p class="card-text">Objekt: {{ device.facility?.name }}</p>
          <p class="card-text">Tip: {{ device.attributes.find((a) => a.name === "UnitType")?.value }}</p>
          <p class="card-text">Podtip: {{ device.attributes.find((a) => a.name === "UnitSubTypes")?.value }}</p>
        </div>
      </div>
    </div>
  </div>

  <RequiredWarning v-show="v.showWarning.value" :missing="v.missing.value" />

  <div class="row mt-5" v-show="!!device">
    <div class="col">
      <div class="table-responsive">
        <table class="table table-stripped table-sm table-hover">
          <thead>
            <tr>
              <th>Št. merila - Displej - termometer</th>
              <th>T<sub>umerjeni</sub></th>
              <th>T<sub>preverjanca</sub></th>
              <th>Korekcija /korektivni ukrep</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr role="button" v-for="(e, i) in entity.entries">
              <td>
                <input
                  class="form-control"
                  :class="{ 'is-invalid': v.invalid(`entry-${i}-thermometerIdent`) }"
                  v-model="e.thermometerIdent"
                  required
                />
              </td>
              <td>
                <input
                  class="form-control"
                  :class="{ 'is-invalid': v.invalid(`entry-${i}-measured`) }"
                  v-model="e.measuredValue"
                  type="text"
                  inputmode="text"
                  required
                />
              </td>
              <td>
                <input
                  class="form-control"
                  :class="{ 'is-invalid': v.invalid(`entry-${i}-reference`) }"
                  v-model="e.referenceValue"
                  type="text"
                  inputmode="text"
                  required
                />
              </td>
              <td><input class="form-control" v-model="e.correctiveAction" /></td>
              <td>
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="entity.entries.splice(entity.entries.indexOf(e), 1)"
                >
                  Izbriši
                </button>
              </td>
            </tr>
            <tr role="button">
              <td colspan="5" class="text-end">
                <button class="btn btn-sm btn-primary" :disabled="!canAddRow" @click="entity.entries.push({})">
                  Dodaj
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <div class="row sticky-actions py-2 bg-light mb-5">
    <div class="col-2">
      <button class="btn btn-danger btn-lg me-3" type="button" v-show="false">Izbriši</button>
    </div>
    <div class="col-10 text-end">
      <button
        class="btn btn-outline-secondary me-3"
        type="button"
        @click="$router.push({ name: 'ServiceOrder', params: { workOrderId: workOrder.id } })"
      >
        Prekliči
      </button>
      <button class="btn btn-success btn-lg me-3" type="button" @click="saveAsync">Shrani</button>
    </div>
  </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useWorkOrder } from "../composition-api/useWorkOrder"
import { useAuth } from "../composition-api/useAuth"
import { DATETIME_FORMAT } from "../utils/constants"
import { getEntityAsync, saveChecklistAsync } from "../api"
import { useValidation, hasText, isSetGuid, isNumber } from "../composition-api/useValidation"

defineOptions({ name: "CalibrationReport" })

const route = VueRouter.useRoute()
const router = VueRouter.useRouter()
const { fetchAuthAsync } = useAuth()

const entity = ref({})
const device = ref(null)
const { devices, workOrder } = useWorkOrder("Service")

const saveAsync = async () => {
  if (!v.attempt()) return
  // create a deep copy and convert measuredValue/referenceValue to numbers (commas -> dots)
  const payload = JSON.parse(JSON.stringify(entity.value || {}))
  payload.entries = (payload.entries || []).map((e) => {
    const measuredRaw = String(e.measuredValue ?? "")
      .replace(",", ".")
      .trim()
    const referenceRaw = String(e.referenceValue ?? "")
      .replace(",", ".")
      .trim()
    const measuredNum = measuredRaw === "" ? null : Number(measuredRaw)
    const referenceNum = referenceRaw === "" ? null : Number(referenceRaw)
    return {
      ...e,
      measuredValue: Number.isNaN(measuredNum) ? null : measuredNum,
      referenceValue: Number.isNaN(referenceNum) ? null : referenceNum,
    }
  })
  const saved = await saveChecklistAsync(fetchAuthAsync, payload)
  if (saved) {
    router.push({ name: `ServiceOrder`, params: { workOrderId: workOrder.value.id } })
  }
}

const loadCalibrationReportAsync = async () => {
  entity.value = await getEntityAsync(
    fetchAuthAsync,
    `CalibrationReport/${route.params.workOrderId}`,
    route.params.deviceId,
  )
  device.value = await getEntityAsync(fetchAuthAsync, "Device", entity.value.deviceID)
  if (entity.value.entries.length < 1) {
    entity.value.entries.push({})
  }
  console.log("Loaded calibration report", entity.value)
  console.log("Loaded device", device.value)
}

const v = useValidation(() => {
  const entries = Array.isArray(entity.value.entries) ? entity.value.entries : []
  const rules = {
    deviceID: [() => isSetGuid(entity.value.deviceID), "Naprava"],
    deviceType: [() => device.value?.typeShort === "THM", "Naprava tipa termometer (THM)"],
    entries: [() => entries.length > 0, "Vsaj ena vrstica"],
  }
  entries.forEach((e, i) => {
    rules[`entry-${i}-thermometerIdent`] = [() => hasText(e.thermometerIdent), `Vrstica ${i + 1}: Št. merila`]
    rules[`entry-${i}-measured`] = [() => isNumber(e.measuredValue), `Vrstica ${i + 1}: T umerjeni`]
    rules[`entry-${i}-reference`] = [() => isNumber(e.referenceValue), `Vrstica ${i + 1}: T preverjanca`]
  })
  return rules
})
// new rows start empty, so adding more only needs the device to be right
const canAddRow = computed(() => !v.fails("deviceID") && !v.fails("deviceType"))

watch(() => route.params.deviceId, loadCalibrationReportAsync)

watch(() => route.params.workOrderId, loadCalibrationReportAsync)

onMounted(async () => loadCalibrationReportAsync())
</script>
